<template>
	<div>
		<v-container>
			<v-card>
				<v-card-title primary-title>
					<v-row align="center" no-gutters>
						<v-col>
							<div class="title">Users</div>
						</v-col>
						<v-col class="shrink">
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<v-btn icon v-on="on" @click="filtering = !filtering">
										<v-icon color="primary">{{ filtering ? "mdi-filter" : "mdi-filter-outline" }}</v-icon>
									</v-btn>
								</template>

								<span>Filter</span>
							</v-tooltip>
						</v-col>
						<v-col class="shrink">
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<v-btn icon color="success" v-on="on" @click="newUser">
										<v-icon color="success">mdi-plus</v-icon>
									</v-btn>
								</template>

								<span>New User</span>
							</v-tooltip>
						</v-col>

						<v-col cols="12">
							<v-text-field
								v-model="search"
								append-icon="$vuetify.icons.search"
								label="Search (Name, ID)"
								single-line
								hide-details
							></v-text-field>
						</v-col>

						<v-col v-if="filtering" cols="12">
							<v-row dense>
								<v-col cols="12" sm="6">
									<v-select v-model="filterClinic" :items="clinics" label="Clinic"></v-select>
								</v-col>

								<v-col cols="12" sm="6">
									<v-select
										v-model="filterUserType"
										:items="allUserTypes"
										label="User Type"
										item-text="description"
										item-value="id"
									></v-select>
								</v-col>

								<v-col class="text-center" cols="12">
									<v-btn color="success" class="text-none mx-2 my-2" disabled @click="applyFilter">Apply Filter</v-btn>
									<v-btn
										color="error"
										class="text-none mx-2 my-2"
										:disabled="!filterEnabled"
										@click="clearFilter"
									>
										<template>Remove Filter</template>
									</v-btn>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text class="pa-0 pt-6">
					<v-data-table
						:headers="headers"
						:items="items"
						:loading="loading"
						disable-sort
						hide-default-footer
						:no-data-text="search.length && search.length < 3 ? 'Search must be at least 3 characters' : 'Your search yielded no results'"
						:items-per-page="-1"
					>
						<template v-slot:item.name="{ item }">
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<a v-on="on" @click="modifyUser(item.nationalId)">{{ item.name }}</a>
								</template>

								<span>Modify</span>
							</v-tooltip>
						</template>

						<template v-slot:item.actions="{ item }">
							<span style="white-space: nowrap;">
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon color="primary" v-on="on" @click="modifyUser(item.nationalId)">mdi-account-edit</v-icon>
									</template>

									<span>Modify</span>
								</v-tooltip>

								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon color="secondary" v-on="on" @click="updatePasswordForUser(item)">mdi-lock-question</v-icon>
									</template>

									<span>Rest Password</span>
								</v-tooltip>

								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon color="error" v-on="on" @click="deleteUser(item)">mdi-delete</v-icon>
									</template>

									<span>Delete</span>
								</v-tooltip>
							</span>
						</template>
					</v-data-table>
				</v-card-text>

				<v-card-actions v-if="length">
					<v-row no-gutters>
						<v-col class="text-center" cols="12">
							<v-pagination v-model="page" :length="length" :disabled="loading" circle color="primary"></v-pagination>
						</v-col>
					</v-row>
				</v-card-actions>
			</v-card>
		</v-container>

		<v-dialog v-model="updatePasswordDialog" transition="dialog-transition" max-width="500">
			<v-card>
				<v-card-text>
					<v-form v-model="valid">
						<v-row v-if="updatingUser" class="text-center">
							<v-col cols="12" class="text-left">
								Updating Password for
								<span
									class="font-weight-bold"
								>{{ updatingUser.name }} ({{ updatingUser.UserType }})</span>
							</v-col>

							<v-col cols="12">
								<v-text-field
									v-model="newPassword"
									label="New Password"
									:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
									:type="showPassword ? 'text' : 'password'"
									:rules="[rules.required, rules.password]"
									@click:append="() => (showPassword = !showPassword)"
									@keydown.enter="updatePassword"
								></v-text-field>
							</v-col>

							<v-col cols="12">
								<v-btn
									color="success"
									class="text-none"
									:loading="passwordUpdating"
									:disabled="!valid"
									@click="updatePassword"
								>
									<template>Update Password</template>
								</v-btn>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
const axios = require("axios");
const debounce = require("lodash/debounce");

export default {
	mixins: [
		require("@/mixins/backend-mixin").default,
		require("@/mixins/router-mixin").default,
		require("@/mixins/rules-mixin").default,
		require("@/mixins/user-types-mixin").default
	],
	data: () => ({
		headers: [
			{
				text: "User ID",
				align: "left",
				value: "patient_id"
			},
			{
				text: "User Name",
				align: "left",
				value: "name"
			},
			{
				text: "Clinic",
				align: "left",
				value: "Clinic"
			},
			{
				text: "Cell Phone",
				align: "left",
				value: "cellphone"
			},
			{
				text: "Email",
				align: "left",
				value: "email"
			},
			{
				text: "Type",
				align: "left",
				value: "UserType"
			},
			{
				text: "Actions",
				align: "center",
				value: "actions",
				sortable: false
			}
		],
		items: [],
		loading: true,
		search: "",
		page: 1,
		length: null,
		clinic: null,
		clinicId: null,
		filtering: false,
		filterEnabled: false,
		filter: {
			state: "0",
			stage: "0",
			filteringDate: "1",
			date: new Date().toISOString().substring(0, 10)
		},
		clinics: [
			"All",
			"Clinic 1",
			"Clinic 2",
			"Clinic 3",
			"Clinic 4",
			"Clinic 5"
		],
		filterClinic: "All",
		filterUserType: "All",
		valid: false,
		newPassword: "",
		showPassword: false,
		updatePasswordDialog: false,
		updatingUser: null,
		passwordUpdating: false
	}),
	computed: {
		allUserTypes() {
			return ["All", ...this.userTypes];
		}
	},
	watch: {
		search() {
			this.items = [];
			this.loading = true;
			this.triggerPageReload();
		},
		page(newVal) {
			this.items = [];
			this.loading = true;
			this.getFilteredPage(newVal);
		}
	},
	created() {
		this.getPage = debounce(this.getPage, 500);
		this.getFilteredPage = debounce(this.getFilteredPage, 500);
	},
	mounted() {
		this.fetchData();
	},
	methods: {
		newUser() {
			this.$router.push("/dashboard/new-user");
		},
		modifyUser(id) {
			this.$router.push("/dashboard/modify-user/" + id);
		},
		deleteUser(user) {
			const thisVue = this;
			const url = this.BACKEND_URL + "/api/destroyUser";

			var data = new FormData();

			data.append("nationalId", user.nationalId);
			
			var token = this.getBackendToken();
			data.append("token", token);

			let config = {};

			axios
				.post(url, data, config)
				.then(response => {
					if (response.status == 200) {
						console.log(response);
						thisVue.items.splice(thisVue.items.indexOf(user), 1);
						thisVue.$store.dispatch("triggerPushNotification", {
							message: "Deleted the account of " + user.name
						});
					}
				})
				.catch(error => {
					console.error(error);
				});
		},
		updatePasswordForUser(user) {
			this.newPassword = "";
			this.updatingUser = user;
			this.updatePasswordDialog = true;
		},
		updatePassword() {
			const url = this.BACKEND_URL + "/api/updateUser";

			this.passwordUpdating = true;

			var data = new FormData();

			data.append("nationalId", this.updatingUser.patient_id);
			console.log(this.updatingUser);
			data.append("password", this.newPassword);

			var token = this.getBackendToken();
			data.append("token", token);

			let thisVue = this;
			let config = {};

			axios
				.post(url, data, config)
				.then(response => {
					console.log(response);
					if (response.status == 200) {
						console.log("200");
						thisVue.$store.commit("pushNotification", {
							message:
								"Password changed for " +
								thisVue.updatingUser.name
						});
						thisVue.passwordUpdating = false;
						this.updatePasswordDialog = false;
					}
				})
				.catch(error => {
					console.error(error);
					thisVue.passwordUpdating = false;
				});
		},
		fetchData() {
			this.getPage(1);
		},
		getPage(page) {
			const thisVue = this;
			const url = this.BACKEND_URL + "/api/allUser?page=" + page;

			axios
				.get(url)
				.then(response => {
					if (response.status == 200) {
						thisVue.loading = false;
						thisVue.length = response.data.last_page;
						thisVue.items = response.data.data.map(item => {
							item.name =
								item.FirstName +
								" " +
								item.FatherName +
								" " +
								item.FamilyName;
							item.patient_id = item.nationalId;
							item.UserType = thisVue.getUserTypeById(
								item.UserType
							);
							return item;
						});
					}
				})
				.catch(error => {
					console.error(error);
				});
		},
		applyFilter() {
			if (this.filterClinic == "All" && this.filterUserType == "All") {
				this.filterEnabled = false;
			} else {
				this.filterEnabled = true;
				this.filter = {
					clinic: this.filterClinic,
					userType: this.filterUserType
				};
			}
			this.getFilteredPage(1);
		},
		clearFilter() {
			this.filterEnabled = false;
			this.filter = {
				clinic: "All",
				userType: "All"
			};
			this.getFilteredPage(1);
		},
		getFilteredPage(page) {
			console.log(this.filterEnabled + "-" + this.search);
			if (!this.filterEnabled) {
				if (!this.search || this.search == null || this.search == "") {
					this.getPage(page);
					return;
				}
			}

			console.log("--------");

			const thisVue = this;
			var name = this.search || "";
			const url =
				this.BACKEND_URL +
				"/api/findUserByNameOrId/" +
				name +
				"?page=" +
				page;

			// var data = {};

			// if (this.filterEnabled) {
			// 	data = { ...data, ...this.filter };
			// }

			// if (this.search) {
			// 	data = { ...data, search: this.search };
			// }

			axios
				.get(url)
				.then(response => {
					if (response.status == 200) {
						thisVue.loading = false;

						if (response.data.data) {
							thisVue.length = response.data.last_page;
							thisVue.items = response.data.data.map(item => {
								item.name =
									item.FirstName +
									" " +
									item.FatherName +
									" " +
									item.FamilyName;
								item.patient_id = item.nationalId;
								item.UserType = this.userTypes[
									item.UserType - 1
								];
								return item;
							});
						} else {
							thisVue.length = 1;
							var patient = { ...response.data };
							patient.name =
								patient.FirstName +
								" " +
								patient.FatherName +
								" " +
								patient.FamilyName;
							patient.patient_id = patient.nationalId;
							patient.UserType = this.userTypes[
								patient.UserType - 1
							];
							thisVue.items = [patient];
						}
					}
				})
				.catch(error => {
					thisVue.loading = false;
					if (error.response.data == "User not found") {
						console.log("Error: User not found");
					} else {
						console.error(error);
					}
				});
		},
		triggerPageReload() {
			if (this.page == 1) {
				this.items = [];
				this.loading = true;
				this.getFilteredPage(1);
			} else {
				this.page = 1;
			}
		}
	}
};
</script>

<style scoped>
.v-datatable thead th.column {
	background-color: #fafafa;
	font-weight: bold;
}

.v-input--selection-controls {
	margin-top: initial;
	padding-top: initial;
}

.v-input--selection-controls__input {
	margin-right: initial;
}
</style>
